import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Gavel, History, Gem, Store, User, LucideIcon, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import PlayerAvatar from "@/components/PlayerAvatar";
import { TierBadge } from "@/components/ui/tier-badge";

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  isExternal?: boolean;
  variant?: "default" | "discord" | "auth";
}

const baseNavItems: (NavItem & { tier?: "alpha" | "beta" })[] = [
  { path: "/", label: "Auktionen", icon: Gavel },
  { path: "/auctions/history", label: "Verlauf", icon: History },
  { path: "/shards", label: "Shards", icon: Gem, tier: "beta" },
  { path: "/market", label: "Market", icon: Store, tier: "alpha" },
];

const HeaderNav = memo(() => {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number } | null>(null);
  const [enableTransition, setEnableTransition] = useState(false);
  
  const { isAuthenticated, isVerified, profile, isLoading } = useAuth();

  // Build nav items dynamically based on auth state
  const navItems: NavItem[] = [
    ...baseNavItems,
    { 
      path: "/dash", 
      label: isAuthenticated ? (isVerified ? "Dashboard" : "Verifizieren") : "Login", 
      icon: isAuthenticated ? LayoutDashboard : User, 
      variant: isAuthenticated ? "auth" : "discord" 
    },
  ];
  
  // Determine active path (excluding discord/auth button from indicator)
  // /panel also activates the /dash tab (they belong together)
  const effectivePath = location.pathname.startsWith("/panel") ? "/dash" : location.pathname;
  const activePath = navItems.find(item => {
    if (item.variant === "discord" || item.variant === "auth") return false;
    if (item.path === "/") return effectivePath === "/" || effectivePath === "/auctions";
    return effectivePath.startsWith(item.path);
  })?.path;

  const measure = useCallback(() => {
    if (!containerRef.current || !activePath) return;

    const activeButton = containerRef.current.querySelector(
      `[data-path="${activePath}"]`
    ) as HTMLElement | null;
    if (!activeButton) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();

    setIndicatorStyle({
      left: buttonRect.left - containerRect.left,
      width: buttonRect.width,
    });
  }, [activePath]);

  // Defer initial measure to after first paint to avoid forced reflow.
  useEffect(() => {
    requestAnimationFrame(() => {
      measure();
      if (!enableTransition) {
        requestAnimationFrame(() => setEnableTransition(true));
      }
    });
  }, [measure, location.pathname, enableTransition]);

  // Keep indicator aligned on resize.
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const isDashActive = location.pathname.startsWith("/dash") || location.pathname.startsWith("/panel");

  return (
    <div className="overflow-x-auto scrollbar-hide">
      <div 
        ref={containerRef}
        className="relative flex items-center gap-1 p-1 bg-secondary/30 rounded-lg w-max"
      >
        {/* Sliding Indicator */}
        {activePath && indicatorStyle && (
          <div
            className={cn(
              "absolute top-1 bottom-1 bg-primary rounded-md",
              enableTransition && "transition-all duration-300 ease-out"
            )}
            style={{
              left: indicatorStyle.left,
              width: indicatorStyle.width,
            }}
          />
        )}
        
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.path === "/" 
            ? location.pathname === "/" 
            : location.pathname.startsWith(item.path);
          const isDiscord = item.variant === "discord";
          const isAuthButton = item.variant === "auth";
          
          // Authenticated user button
          if (isAuthButton && isAuthenticated && !isLoading) {
            return (
              <Link
                key={item.path}
                to={item.path}
                data-path={item.path}
                className={cn(
                  "relative z-10 flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ml-1",
                  isDashActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/50 hover:bg-muted text-foreground"
                )}
              >
                {profile?.game_uuid ? (
                  <PlayerAvatar uuid={profile.game_uuid} size={24} className="rounded-md" />
                ) : (
                  <Icon size={16} />
                )}
                <span className="hidden sm:inline">{item.label}</span>
              </Link>
            );
          }
          
          return (
            <Link
              key={item.path}
              to={item.path}
              data-path={item.path}
              className={cn(
                "relative z-10 flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap",
                isDiscord
                  ? "bg-discord hover:bg-discord-hover text-discord-foreground ml-1"
                  : isActive
                    ? "text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{item.label}</span>
              {(item as any).tier && (
                <TierBadge tier={(item as any).tier} size="xs" />
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
});

HeaderNav.displayName = 'HeaderNav';

export default HeaderNav;
